import type { NotificationChannel, NotificationPreferenceWire } from '../../lib/api-client';

/**
 * auxiliar-adapter — lógica PURA da Tela 6 (Auxiliar). Sem React, testável.
 *
 * Notificações (card REAL): o backend (`notification-preferences`) não expõe um
 * catálogo de templates nem campo de audiência — e tem 31 slugs, a maioria
 * supervisor/DPO/lume-internos. Curamos aqui o subconjunto **atendente-relevante**
 * (slugs REAIS de `NotificationTemplate`), canal `in_app` (o que "interrompe em
 * foco"). Rótulos descrevem o evento real do template — NÃO mecânicas de entrega
 * fabricadas. Estado efetivo segue a convenção do backend: ausência de row =
 * enabled (default opt-in); só opt-out/in EXPLÍCITO no canal in_app altera.
 */

/** Canal do card de Notificações (o que interrompe em foco). */
export const NOTIFICATION_CHANNEL: NotificationChannel = 'in_app';

export interface NotificationCatalogEntry {
  /** Slug REAL do NotificationTemplate (Engine). */
  templateId: string;
  label: string;
  sub: string;
}

/** Subconjunto curado de templates REAIS relevantes ao atendente (ordem = UI). */
export const NOTIFICATION_CATALOG: readonly NotificationCatalogEntry[] = [
  {
    templateId: 'p1-elevated',
    label: 'Conversa elevada a P1',
    sub: 'prioridade crítica numa conversa sua',
  },
  {
    templateId: 'handoff-requested',
    label: 'Transferência solicitada',
    sub: 'pedido de transferência de uma conversa',
  },
  {
    templateId: 'sla-breach',
    label: 'SLA estourado',
    sub: 'uma conversa ultrapassou o tempo de resposta',
  },
  {
    templateId: 'pendency-overdue-warning',
    label: 'Pendência vencendo',
    sub: 'uma pendência sua está perto do prazo',
  },
  {
    templateId: 'conflict-alert-detected',
    label: 'Alerta de conflito detectado',
    sub: 'conflito de interesse num vínculo',
  },
];

export interface NotificationRow extends NotificationCatalogEntry {
  /** Estado efetivo do toggle (default-ON salvo opt-out explícito in_app). */
  enabled: boolean;
}

/**
 * Resolve as linhas do card de Notificações a partir das prefs do backend.
 * Default opt-in: sem row no canal in_app → enabled=true. Só o canal in_app
 * importa (o card é de interrupção em foco). Prefs fora do catálogo são ignoradas.
 */
export function resolveNotificationRows(
  prefs: readonly NotificationPreferenceWire[],
  catalog: readonly NotificationCatalogEntry[] = NOTIFICATION_CATALOG,
): NotificationRow[] {
  return catalog.map((entry) => {
    const explicit = prefs.find(
      (p) => p.channel === NOTIFICATION_CHANNEL && p.templateId === entry.templateId,
    );
    return { ...entry, enabled: explicit != null ? explicit.enabled : true };
  });
}

const ROLE_LABELS: Record<string, string> = {
  atendente: 'Atendente',
  supervisor: 'Supervisor',
  gestor: 'Gestor',
  admin_tenant: 'Administrador',
  dpo_tenant: 'DPO',
  suporte_lume: 'Suporte Lume',
  engenharia_lume: 'Engenharia Lume',
  direcao_lume: 'Direção Lume',
};

/** Rótulo PT do papel; fallback honesto ao próprio código se desconhecido. */
export function roleLabel(primaryRole: string): string {
  return ROLE_LABELS[primaryRole] ?? primaryRole;
}
